import { Progress } from "@/components/ui/progress";
import { MemoryEmotionIndicator } from "@/components/ui/indicator";
import { useReportsContext } from "../../contexts/reports-context";
import { AssetInsight } from "@/api/studio/insights/types/insight.type";
import { ReactNode } from "react";
import { EmotionScore } from "@/api/studio/insights/types/emotion.type";
import { MemoryScore } from "@/api/studio/insights/types/memory.type";

/* Prop Types */
interface KeyDriverMetricTabProps {
  onClick: () => void;
  metric: string;
  metricIcon: ReactNode;
  currentAsset: AssetInsight;
  reportType: "attention" | "emotion" | "memory";
}

/*
 * KeyDriverMetricTab component
 *
 * This component represents a tab for a specific key driver metric within the in-depth report.
 * It displays the metric name, an icon, and a progress bar (for attention) or indicator (for emotion/memory).
 * Used to swap between different key driver metrics like Visuals, Copy, Branding, etc.
 */
export const KeyDriverMetricTab = ({ onClick, metric, metricIcon, currentAsset, reportType }: KeyDriverMetricTabProps) => {
  /* Context */
  const { state } = useReportsContext();

  /* Helpers */
  const toTitleCase = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getScore = () => {
    if (reportType === "attention") {
      switch (metric) {
        case "visibility":
          return currentAsset?.metrics?.attention?.visibility?.score;
        case "clarity":
          return currentAsset?.metrics?.attention?.clarity?.score || 0;
        case "digestibility":
          return currentAsset?.metrics?.attention?.digestibility?.score || 0;
        default:
          return null;
      }
    } else if (reportType === "emotion") {
      switch (metric) {
        case "visuals":
          return currentAsset?.metrics?.emotion?.visual;
        case "text":
          return currentAsset?.metrics?.emotion?.text;
        default:
          return null;
      }
    } else if (reportType === "memory") {
      switch (metric) {
        case "branding":
          return currentAsset?.metrics?.memory?.branding;
        case "visuals":
          return currentAsset?.metrics?.memory?.visual;
        case "text":
          return currentAsset?.metrics?.memory?.text;
        default:
          return null;
      }
    }
    return 0;
  };

  const isActive = () => {
    if (reportType === "attention") {
      return state.attentionMetric === metric;
    } else if (reportType === "emotion") {
      return state.emotionMetric === metric;
    } else if (reportType === "memory") {
      return state.memoryMetric === metric;
    }
    return false;
  };

  /* Consts */
  const score = getScore();

  /* Render */
  return (
    <div
      className={`flex flex-col gap-4 grow p-3 cursor-pointer rounded-lg progress-parent-hover transition-colors duration-200 ${isActive() ? "bg-base-100 drop-shadow-md" : "hover:bg-base-100/40"}`}
      onClick={onClick}
    >
      <div className="flex flex-row items-center gap-2">
        {metricIcon}
        <p>{toTitleCase(metric)}</p>
      </div>
      {reportType === "attention" && score ? (
        <div className="flex flex-row items-center gap-2">
          <Progress value={score} className="w-full mr-5" disabled={!isActive()} />
          <p className="text-sm font-medium ml-2 whitespace-nowrap">{score}%</p>
        </div>
      ) : (
        <MemoryEmotionIndicator score={score as EmotionScore | MemoryScore} type={reportType as "emotion" | "memory"} size="small" />
      )}
    </div>
  );
};
